import { Item as RadixItem } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  forwardRef,
  useContext,
  useState,
  type ElementType,
  type ReactElement,
} from 'react';

import { MobileMenuModeContext } from './contexts/MobileMenuModeContext.js';
import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { useMenuContext } from './hooks/useMenuContext.js';
import { getItemSlots } from './item-slots.js';
import { MenuItemTooltipWrapper } from './ItemTooltip.js';
import * as styles from './Menu.sty.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useTooltipState } from '../../overlays/hooks/useTooltipState.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

/**
 * Props for the Menu.Link component
 * @public
 */
export interface MenuLinkOwnProps extends WithChildren, AriaLabelingProps {
  /**
   * Whether the menu link is disabled.
   * If `true`, it prevents the user from interacting with the link and the link
   * being focused.
   */
  disabled?: boolean;
  /**
   * Optional text used for typeahead purposes. By default, the typeahead
   * behavior will use the `.textContent` of the link. Use this when the content
   * is complex, or you have non-textual content inside.
   */
  textValue?: string;
}

/**
 * @public
 */
export type MenuLinkProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  MenuLinkOwnProps
>;

/**
 * The component that contains the dropdown menu Links.
 * @public
 */
export const Link: <E extends ElementType = 'a'>(
  props: MenuLinkProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      children,
      disabled = false,
      textValue,
      as = 'a' as E,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    }: MenuLinkProps<E>,
    forwardedRef: typeof remainingProps.ref,
  ) => {
    const isMobile = useContext(MobileMenuModeContext);
    const { setIsOpen } = useMenuContext();
    const slots = getItemSlots(children);

    const {
      isOpen,
      open: openTooltip,
      close: closeTooltip,
    } = useTooltipState({
      defaultOpen: false,
    });

    const [tooltipTriggerRef, setTooltipTriggerRef] =
      useState<HTMLElement | null>(null);

    const { focusProps, focusClassName } = useFocusRing({
      disabled,
    });
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();

    if (isMobile) {
      return (
        <Flex
          ref={forwardedRef}
          role="menuitem"
          aria-disabled={disabled}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          className={clsx(
            textStyleCSS({ textStyle: 'base-emphasized' }),
            !isItemHovered && focusClassName,
            styles.item({ disabled }),
            consumerClassName,
          )}
          style={consumerStyle}
          disabled={disabled}
          as={as satisfies ElementType}
          {..._mergeProps(focusProps, hoverProps, {
            onClick: () => {
              if (!disabled) {
                setIsOpen(false);
              }
            },
          })}
          {...remainingProps}
        >
          {slots.prefix}
          <div className={styles.itemChildren}>{slots.freeform}</div>
          {slots.suffix}
        </Flex>
      );
    }

    return (
      <>
        <RadixItem
          ref={forwardedRef}
          textValue={textValue}
          {..._mergeProps(focusProps, hoverProps, {
            onFocus: () => slots.tooltip && !disabled && openTooltip(true),
            onBlur: () => slots.tooltip && !disabled && closeTooltip(),
          })}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          role="menuitem"
          asChild
        >
          <Flex
            {...remainingProps}
            className={clsx(
              textStyleCSS({ textStyle: 'base-emphasized' }),
              !isItemHovered && focusClassName,
              styles.item({ disabled }),
              consumerClassName,
            )}
            style={consumerStyle}
            as={as satisfies ElementType}
            ref={setTooltipTriggerRef}
            disabled={disabled}
            aria-disabled={disabled}
          >
            {slots.prefix}
            <div className={styles.itemChildren}>{slots.freeform}</div>
            {slots.suffix}
          </Flex>
        </RadixItem>
        {isOpen && (
          <MenuItemTooltipWrapper triggerElement={tooltipTriggerRef}>
            {slots.tooltip}
          </MenuItemTooltipWrapper>
        )}
      </>
    );
  },
);

(Link as typeof Link & { displayName: string }).displayName = 'Menu.Link';
